import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { FileUploadManagerRef } from "../../../components/forms/fileUploadManager";
import Header from "./header";
import styles from "./styles.module.scss";
import StepOne from "./stepOne";
import StepTwo from "./stepTwo";
import StepThree from "./stepThree";
import StepFour from "./stepFour";
import StepFive from "./stepFive";
import { ROUTES } from "../../../setup/constants";
import { useNavigate, useParams } from "react-router-dom";
import {
  addContractMilestonesExternal,
  createExternalContractDetails,
  editContractPartiesExternal,
  editExternalContractDetails,
  editExternalDraftContractParties,
  getExternalContractDetails,
  getExternalMilestoneContracts,
  publishExternalDraftContract,
} from "../../../setup/api/services/contract";
import {
  ESenderRole,
  EShipmentType,
  EPaymentType,
  EPaymentBrand,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import {
  complexContractBody,
  milestoneContractBody,
} from "../../../setup/util/helpers";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import { TFunction } from "i18next";
import { arraysAreEqual } from "../../../setup/util/arraysEqual";
import { milestonePartiesEqual } from "../../../setup/util/milestonePartiesEqual";
import { ECONTRACT_STATUS } from "../../../enums";
import Spinner from "../../../components/common/Spinner";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";
import {
  getOrganization,
  getOrganizationPaymentMethods,
} from "../../../setup/api/services/organization";
import { IOrganizations } from "../../../setup/types/Organisation";

const buildSchema = (t: TFunction) =>
  z.object({
    currentMilestoneIndex: z.number(),
    title: z
      .string()
      .trim()
      .min(5, { message: t("err_min_chars", { min: 5 }) })
      .max(100, { message: t("err_max_chars", { max: 100 }) })
      .nonempty({ message: t("err_required") }),
    description: z
      .string()
      .max(2000, { message: t("err_max_chars", { max: 2000 }) }),
    milestones: z.array(
      z
        .object({
          title: z
            .string()
            .trim()
            .min(5, { message: t("err_min_chars", { min: 5 }) })
            .max(100, { message: t("err_max_chars", { max: 100 }) })
            .nonempty({ message: t("err_required") }),
          paymentMethods: z.object({
            cards: z.array(z.nativeEnum(EPaymentBrand)),
            apple_pay: z.array(z.nativeEnum(EPaymentBrand)),
            tabby: z.array(z.nativeEnum(EPaymentBrand)),
            bank_transfer: z.array(z.nativeEnum(EPaymentBrand)),
            stc_pay: z.array(z.nativeEnum(EPaymentBrand)),
          }),
          deadline: z
            .string()
            .nonempty({ message: t("err_deadline_required") }),
          milestoneReleaseTerms: z
            .string()
            .max(2000, { message: t("err_release_terms_max") })
            .optional(),
          milestoneCancelTerms: z
            .string()
            .max(2000, { message: t("err_cancel_terms_max") })
            .optional(),
          buyerOnly: z.boolean().optional(),
          isDestination: z.boolean().optional(),
          sourceContractId: z.string().optional(),
          images: z.array(z.string()).optional(),
          parties: z.array(
            z
              .object({
                id: z.string().optional(),
                name: z
                  .string()
                  .trim()
                  .optional()
                  .superRefine((val, ctx) => {
                    if (!val) return; // skip empty or undefined

                    if (val.length < 5) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.too_small,
                        origin: "string",
                        minimum: 5,
                        inclusive: true,
                        type: "string",
                        message: t("err_min_chars", { min: 5 }),
                      });
                    } else if (val.length > 100) {
                      ctx.addIssue({
                        code: z.ZodIssueCode.too_big,
                        origin: "string",
                        maximum: 100,
                        inclusive: true,
                        type: "string",
                        message: t("err_max_chars", { max: 100 }),
                      });
                    }
                  }),
                phoneNumber: z.string().optional(),
                amount: z.number(),
                fee: z.number().optional(),
                role: z.string().optional(),
                type: z.string().nonempty({ message: t("err_required") }),
                isCompany: z.boolean().optional(),
                companyUnifiedNumber: z.string().optional(),
                companyName: z.string().optional(),
              })
              .superRefine((data, ctx) => {
                if (data.role === ESenderRole.BROKER) {
                  return;
                }
                if (
                  (data?.type === "customer" && data.amount < 1) ||
                  data.amount < 0
                ) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["amount"],
                    message: t("err_number_min", {
                      min: data?.type === "customer" ? 1 : 0,
                    }),
                  });
                }
                if (data.amount > 1000000000) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["amount"],
                    message: t("err_number_max", { max: 1000000000 }),
                  });
                }
              })
          ),
        })
        .superRefine((milestone, ctx) => {
          if (milestone.isDestination) return;
          const pm = milestone.paymentMethods;
          if (
            !pm ||
            !Object.values(pm).some(
              (arr) => Array.isArray(arr) && arr.length > 0
            )
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["paymentMethods"],
              message: "يجب اختيار وسيلة دفع واحدة على الأقل",
            });
          }
        })
    ),
  });

export type ComplexContractInputs = {
  title: string;
  description: string;
  currentMilestoneIndex: number;
  milestones: {
    title: string;
    paymentMethods: {
      cards: EPaymentBrand[];
      apple_pay: EPaymentBrand[];
      tabby: EPaymentBrand[];
      bank_transfer: EPaymentBrand[];
      stc_pay: EPaymentBrand[];
    };
    images?: string[];
    deadline: string;
    milestoneReleaseTerms?: string;
    milestoneCancelTerms?: string;
    buyerOnly?: boolean;
    isDestination?: boolean;
    sourceContractId?: string;
    parties: {
      id?: string;
      name?: string;
      phoneNumber?: string;
      amount: number;
      fee?: number;
      role?: string;
      type: string;
      isCompany?: boolean;
      companyUnifiedNumber?: string;
      companyName?: string;
    }[];
  }[];
};

export type DraftCreatedContract = {
  createdComplex: any;
  createdMilestones: any;
};

const CreateContract = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { complexId, milestoneId } = useParams();
  const isEditMode = !!complexId && !!milestoneId;

  const userToken = AuthConfig.getToken();
  const organizationCode = useOrganizationCode(userToken) || undefined;
  const [organization, setOrganization] = useState<IOrganizations | undefined>(
    undefined
  );
  // Ref so fetchMilestoneDetails can read the org without being in its dep array
  const organizationRef = useRef<IOrganizations | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const fileUploadref = useRef<FileUploadManagerRef>(null);
  const methods = useForm<ComplexContractInputs>({
    resolver: zodResolver(buildSchema(t)),
    mode: "onChange",
    defaultValues: {
      currentMilestoneIndex: 0,
      title: "",
      description: "",
      milestones: [
        {
          deadline: new Date()?.toString(),
          paymentMethods: {
            cards: [],
            tabby: [],
            apple_pay: [],
            bank_transfer: [],
            stc_pay: [],
          },
        },
      ],
    },
  });

  const isAddMilestone =
    (!!complexId && !milestoneId) ||
    (!complexId && !milestoneId && methods?.watch("currentMilestoneIndex") > 0);

  const {
    setValue: setDraftFormValue,
    watch: draftWatch,
    reset: draftReset,
  } = useForm<DraftCreatedContract>({
    defaultValues: {
      createdComplex: undefined,
      createdMilestones: [],
    },
  });

  const {
    handleSubmit,
    setError,
    formState: { isDirty },
    watch,
  } = methods;

  useEffect(() => {
    if (!organizationCode) return;
    getOrganization(organizationCode).then((res) => {
      organizationRef.current = res?.data;
      setOrganization(res?.data);
    });
  }, [organizationCode]);

  const fetchMilestoneDetails = useCallback(async () => {
    setResetLoading(true);

    try {
      const complexResponse = await getExternalContractDetails(complexId || "");
      const response = await getExternalMilestoneContracts({
        complexParentId: complexId || "",
      });

      const currentIndex = response?.data?.content?.findIndex(
        (milestone) => milestone?.id === milestoneId
      );

      if (
        response?.data?.content?.[currentIndex]?.status ===
        ECONTRACT_STATUS.DRAFT
      ) {
        setCurrentStep(
          +(response?.data?.content?.[currentIndex]?.currentStep || 1)
        );
      } else {
        setCurrentStep(1);
      }

      // When adding a new milestone (isAddMilestone is true), we should directly set to content.length
      // When editing an existing milestone, we should use the found index
      const newMilestoneIndex = isAddMilestone
        ? response?.data?.content?.length
        : currentIndex !== -1
        ? currentIndex
        : 0;

      const dedupMilestoneParties = (
        milestoneParties: Partial<UserProfile>[]
      ) => {
        const orgId = String(
          organizationRef.current?.accountUserIdentifier ?? ""
        );
        // Use role only — ALL server parties (including customers) can have userId set
        const brokerParties = milestoneParties?.filter(
          (p) => p?.role === ESenderRole.BROKER
        );
        const orgBroker =
          brokerParties?.find((p) => p?.userId === orgId) ||
          brokerParties?.[brokerParties.length - 1];

        // Dedup non-broker parties by role+identity (unifiedNumber for companies,
        // phoneNumber for individuals). Keying on both prevents false-dedup when the
        // same person is legitimately a customer AND a provider.
        const seenKeys = new Set<string>();
        const dedupedNonBroker = (
          milestoneParties?.filter((p) => p?.role !== ESenderRole.BROKER) || []
        ).filter((p) => {
          const identity = String(p?.phoneNumber || "").replaceAll(" ", "");
          if (!identity) return true;
          const key = `${p.role ?? ""}:${identity}`;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);
          return true;
        });

        return [...dedupedNonBroker, ...(orgBroker ? [orgBroker] : [])];
      };

      const newMilestones = response?.data?.content?.map((milestone) => {
        const savedStepNum = +(milestone?.currentStep || 0);
        const rawParties = milestone?.parties || [];
        const hasCustomers = rawParties.some(
          (p) => p?.role === ESenderRole.CUSTOMER
        );
        const hasNonBrokerProviders = rawParties.some(
          (p) => p?.role === ESenderRole.PROVIDER
        );
        const inferredIsBuyerOnly =
          savedStepNum >= 4 && !hasNonBrokerProviders && hasCustomers;

        return {
          title: milestone?.itemDetail?.title,
          paymentMethods: {
            cards:
              (milestone?.paymentMethods?.[EPaymentType.MOYASAR] || [])
                ?.length > 0
                ? milestone?.paymentMethods?.[EPaymentType.MOYASAR]
                : [],
            apple_pay:
              (milestone?.paymentMethods?.[EPaymentType.APPLE_PAY] || [])
                ?.length > 0
                ? milestone?.paymentMethods?.[EPaymentType.APPLE_PAY]
                : [],
            tabby:
              (milestone?.paymentMethods?.[EPaymentType.TABBY] || [])?.length >
              0
                ? milestone?.paymentMethods?.[EPaymentType.TABBY]
                : [],
            bank_transfer:
              (
                milestone?.paymentMethods?.[
                  EPaymentType.MANUAL_BANK_TRANSFER
                ] || []
              )?.length > 0
                ? milestone?.paymentMethods?.[EPaymentType.MANUAL_BANK_TRANSFER] // this will be Bank transfer not manual bank transfer
                : [],
            stc_pay:
              (milestone?.paymentMethods?.[EPaymentType.STCPAY] || [])?.length >
              0
                ? milestone?.paymentMethods?.[EPaymentType.STCPAY]
                : [],
          },
          images: [
            ...(milestone?.itemDetail?.images || []),
            ...(milestone?.itemDetail?.attachments || []),
          ],
          parties: dedupMilestoneParties(milestone?.parties || [])?.map(
            (party, index) => {
              const isBroker = party?.role === ESenderRole.BROKER;
              const baseType =
                party?.role === ESenderRole.CUSTOMER ? "customer" : "provider";
              const priorSameTypeCount =
                milestone?.parties?.slice(0, index)?.filter((p) => {
                  const pType =
                    p?.role === ESenderRole.CUSTOMER ? "customer" : "provider";
                  return pType === baseType;
                }).length || 0;
              const typeIndex = isBroker ? 0 : priorSameTypeCount;
              const uniqueSalt = Math.random().toString(36).substring(2, 9);
              const id = `${baseType}-${typeIndex}-${Date.now()}-${uniqueSalt}`;
              const rowId = `${baseType}-${typeIndex}-${Date.now()}-${uniqueSalt}`;
              return {
                id,
                rowId,
                name:
                  milestone?.partiesProfiles?.[party?.role || ""]?.find(
                    (partyUser) => partyUser?.phoneNumber === party?.phoneNumber
                  )?.profile?.fullName ||
                  party?.sudoName ||
                  "",
                phoneNumber: isBroker
                  ? party?.userId || party?.phoneNumber?.replaceAll(" ", "")
                  : party?.phoneNumber?.replaceAll(" ", ""),
                type:
                  party?.role === ESenderRole.CUSTOMER
                    ? "customer"
                    : "provider",
                role: isBroker
                  ? ESenderRole.BROKER
                  : party?.sudoRole || party?.role?.toLowerCase(),
                amount: party?.amount || 0,
                ...(party?.companyUnifiedNumber
                  ? {
                      isCompany: true,
                      companyUnifiedNumber: party?.companyUnifiedNumber || "",
                      companyName: party?.companyName || party?.sudoName || "",
                    }
                  : {}),
                ...(isBroker
                  ? {
                      arbitrator: true,
                      isSender: true,
                      role: ESenderRole.BROKER,
                    }
                  : {}),
              };
            }
          ),
          deadline: new Date(milestone?.deadLine || "")?.toString(),
          milestoneCancelTerms: milestone?.cancellationConditions || "",
          milestoneReleaseTerms: milestone?.acceptanceConditions || "",
          buyerOnly: milestone?.buyerOnly ?? inferredIsBuyerOnly,
          sourceContractId: milestone?.sourceContractId || "",
          isDestination: !!milestone?.sourceContractId,
        };
      });

      const paymentMethodsResponse = await getOrganizationPaymentMethods(
        organizationCode as string
      );

      methods?.reset({
        title: complexResponse?.data?.itemDetail?.title,
        description: complexResponse?.data?.itemDetail?.description,
        currentMilestoneIndex: newMilestoneIndex,
        milestones: isAddMilestone
          ? [
              ...newMilestones,
              {
                title: "",
                parties: [],
                images: [],
                paymentMethods: {
                  cards:
                    (
                      paymentMethodsResponse.data.paymentMethods?.[
                        EPaymentType.MOYASAR
                      ] || []
                    )?.length > 0
                      ? paymentMethodsResponse.data.paymentMethods?.[
                          EPaymentType.MOYASAR
                        ]?.filter(
                          (method) => method !== EPaymentBrand.APPLE_PAY
                        )
                      : [],
                  apple_pay:
                    (
                      paymentMethodsResponse.data.paymentMethods?.[
                        EPaymentType.APPLE_PAY
                      ] || []
                    )?.length > 0
                      ? paymentMethodsResponse.data.paymentMethods?.[
                          EPaymentType.APPLE_PAY
                        ]?.filter(
                          (method) => method !== EPaymentBrand.APPLE_PAY
                        )
                      : [],
                  tabby:
                    (
                      paymentMethodsResponse.data.paymentMethods?.[
                        EPaymentType.TABBY
                      ] || []
                    )?.length > 0
                      ? paymentMethodsResponse.data.paymentMethods?.[
                          EPaymentType.TABBY
                        ]
                      : [],
                  bank_transfer:
                    (
                      paymentMethodsResponse.data.paymentMethods?.[
                        EPaymentType.MANUAL_BANK_TRANSFER
                      ] || []
                    )?.length > 0
                      ? paymentMethodsResponse.data.paymentMethods?.[
                          EPaymentType.MANUAL_BANK_TRANSFER
                        ] // this will be Bank transfer not manual bank transfer
                      : [],
                  stc_pay:
                    (
                      paymentMethodsResponse.data.paymentMethods?.[
                        EPaymentType.STCPAY
                      ] || []
                    )?.length > 0
                      ? paymentMethodsResponse.data.paymentMethods?.[
                          EPaymentType.STCPAY
                        ]
                      : [],
                },
                milestoneCancelTerms: "",
                milestoneReleaseTerms: "",
                deadline: new Date()?.toString(),
              },
            ]
          : newMilestones,
      });

      draftReset({
        createdComplex: complexResponse?.data,
        // TODO: There is missing mapping for the parties
        createdMilestones: response?.data?.content,
      });

      setResetLoading(false);
    } catch (error) {
      navigate(ROUTES.CONTRACT_NOT_FOUND);
      console.error(error);
    }
  }, [
    complexId,
    draftReset,
    isAddMilestone,
    methods,
    milestoneId,
    navigate,
    organizationCode,
  ]);

  useEffect(() => {
    if (complexId && !watch("title")) fetchMilestoneDetails();
  }, [complexId, fetchMilestoneDetails, watch]);

  const isEditCreatedMilestone =
    draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`) &&
    draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`).status &&
    draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
      ?.status !== ECONTRACT_STATUS.DRAFT;

  // No-op function for when draft saving is disabled
  const noOpDraftSave = () => {
    // Intentionally empty - draft saving disabled for created milestones
  };

  // Clean function for editing created milestones (not draft-related)
  const editCreatedMilestone = async () => {
    try {
      setLoading(true);
      const currentMilestoneIndex = watch("currentMilestoneIndex");
      const milestone = watch(`milestones.${currentMilestoneIndex}`);

      const response = await editExternalContractDetails(
        {
          ...milestoneContractBody(
            ESenderRole.BROKER,
            milestone?.title,
            milestone?.images?.filter((file) => !file?.endsWith("pdf")) || [],
            milestone?.images?.filter((file) => file?.endsWith("pdf")) || [],
            milestone?.parties
              ?.filter((party) => party?.type === "customer")
              ?.reduce((acc, cur) => +acc + +cur?.amount, 0),
            milestone?.paymentMethods,
            milestone?.deadline,
            milestone?.milestoneCancelTerms,
            milestone?.milestoneReleaseTerms,
            false, // Not a draft - this is an edit to a created milestone,
            draftWatch(`createdMilestones.${currentMilestoneIndex}`)
              ?.isAcceptable,
            draftWatch(`createdMilestones.${currentMilestoneIndex}`)
              ?.isInspectable
          ),
          ...(milestone?.buyerOnly ? { buyerOnly: true } : {}),
          ...(milestone?.sourceContractId
            ? { sourceContractId: milestone.sourceContractId }
            : {}),
          ...(milestone?.isDestination
            ? {
                paymentMethods: {
                  [EPaymentType.CONTRACT_TRANSFER]: [
                    EPaymentBrand.CONTRACT_TRANSFER,
                  ],
                },
              }
            : {}),
        },
        milestoneId || ""
      );

      if (
        !milestonePartiesEqual(
          draftWatch("createdMilestones")?.[
            currentMilestoneIndex
          ]?.parties?.filter((party) => party?.role === ESenderRole.CUSTOMER),
          milestone?.parties?.filter((party) => party?.type === "customer")
        ) ||
        !milestonePartiesEqual(
          draftWatch("createdMilestones")?.[
            currentMilestoneIndex
          ]?.parties?.filter((party) => party?.role === ESenderRole.PROVIDER),
          milestone?.parties?.filter(
            (party) =>
              party?.type === "provider" && party?.role !== ESenderRole.BROKER
          )
        ) ||
        !milestonePartiesEqual(
          draftWatch("createdMilestones")?.[
            currentMilestoneIndex
          ]?.parties?.filter((party) => party?.role === ESenderRole.BROKER),
          milestone?.parties?.filter(
            (party) => party?.role === ESenderRole.BROKER
          )
        )
      ) {
        const partiesMap = {
          [draftWatch("createdMilestones")?.[currentMilestoneIndex]?.id]: [
            ...(milestone?.parties?.map((party) => ({
              ...(party?.role === ESenderRole.BROKER
                ? { userId: (party?.phoneNumber || "")?.toString()?.trim() }
                : {
                    phoneNumber: (party?.phoneNumber || "")
                      ?.toString()
                      ?.trim()
                      ?.startsWith("+966")
                      ? (party?.phoneNumber || "")?.toString()?.trim()
                      : `+966${(party?.phoneNumber || "")?.toString()?.trim()}`,
                  }),
              role: party?.type?.toUpperCase() as ESenderRole,
              amount: party?.amount,
              sudoName: party?.name,
              sudoRole: party?.role,
              ...(party?.fee ? { fee: party?.fee } : {}),
              ...(party?.role === ESenderRole.BROKER
                ? {
                    role: ESenderRole.BROKER,
                    arbitrator: true,
                    isSender: true,
                  }
                : {}),
              ...(party?.companyUnifiedNumber
                ? {
                    companyUnifiedNumber: party?.companyUnifiedNumber,
                    companyName: party?.companyName,
                  }
                : {}),
            })) || []),
          ],
        };

        await editContractPartiesExternal(
          draftWatch("createdComplex")?.id || "",
          {
            mileStonesParties: partiesMap || {},
          }
        );
      }

      navigate(ROUTES.CONTRACT_DETAILS?.replace(":id", response?.data?.id));
    } catch (error) {
      console.error("Error editing created milestone:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit: SubmitHandler<ComplexContractInputs> = (data) =>
    console.log(data);

  const handleFormSubmit = async () => {
    const currentMilestoneId = draftWatch(
      `createdMilestones.${watch("currentMilestoneIndex")}`
    )?.id;
    try {
      setLoading(true);

      // Add Milestones to Draft contract
      // Creation / Edit Draft (Should edit draft access step six?)
      if (
        draftWatch("createdComplex")?.id &&
        draftWatch("createdMilestones")?.length !== 0 &&
        currentMilestoneId
      ) {
        await publishExternalDraftContract(currentMilestoneId);
      }

      navigate(ROUTES.CONTRACT_DETAILS?.replace(":id", currentMilestoneId));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const draftStepOne = async () => {
    try {
      setLoading(true);
      const contractData = complexContractBody(
        ESenderRole.BROKER,
        watch("title"),
        watch("description"),
        EShipmentType.NONE,
        true
      );

      let response;
      if (!draftWatch("createdComplex"))
        response = await createExternalContractDetails(contractData);
      else if (
        watch("title") !== draftWatch("createdComplex")?.itemDetail?.title ||
        watch("description") !==
          draftWatch("createdComplex")?.itemDetail?.description
      )
        response = await editExternalContractDetails(
          contractData,
          draftWatch("createdComplex")?.id
        );

      if (response) setDraftFormValue("createdComplex", response?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // TODO: In both withErrors check, you don't need to call post draft parties if there are no changes

  const shouldBeDraft =
    isEditMode &&
    draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
      ?.status === ECONTRACT_STATUS.DRAFT;
  const isBEDraft =
    shouldBeDraft || isAddMilestone || (!isEditMode && !isAddMilestone);

  const draftStepTwo = async () => {
    try {
      setLoading(true);
      let response;
      const milestone = watch(`milestones.${watch("currentMilestoneIndex")}`);

      if (
        draftWatch("createdMilestones")?.length === 0 ||
        watch("currentMilestoneIndex") >
          draftWatch("createdMilestones")?.length - 1
      )
        response = await addContractMilestonesExternal(
          draftWatch("createdComplex")?.id,
          {
            parentContractId: draftWatch("createdComplex")?.id,
            milestones: [
              {
                ...milestoneContractBody(
                  ESenderRole.BROKER,
                  milestone?.title,
                  milestone?.images?.filter((file) => !file?.endsWith("pdf")) ||
                    [],
                  milestone?.images?.filter((file) => file?.endsWith("pdf")) ||
                    [],
                  milestone?.parties
                    ?.filter((party) => party?.type === "customer")
                    ?.reduce((acc, cur) => +acc + +cur?.amount, 0),
                  milestone?.paymentMethods,
                  milestone?.deadline,
                  milestone?.milestoneCancelTerms,
                  milestone?.milestoneReleaseTerms,
                  isBEDraft,
                  draftWatch(
                    `createdMilestones.${watch("currentMilestoneIndex")}`
                  )?.isAcceptable,
                  draftWatch(
                    `createdMilestones.${watch("currentMilestoneIndex")}`
                  )?.isInspectable,
                  currentStep.toString()
                ),
                ...(milestone?.buyerOnly ? { buyerOnly: true } : {}),
                ...(milestone?.sourceContractId
                  ? { sourceContractId: milestone.sourceContractId }
                  : {}),
                ...(milestone?.isDestination
                  ? {
                      paymentMethods: {
                        [EPaymentType.CONTRACT_TRANSFER]: [
                          EPaymentBrand.CONTRACT_TRANSFER,
                        ],
                      },
                    }
                  : {}),
              },
            ],
          }
        );
      else if (
        watch(`milestones.${watch("currentMilestoneIndex")}`)?.title !==
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.itemDetail?.title ||
        !arraysAreEqual(
          watch(`milestones.${watch("currentMilestoneIndex")}`)?.paymentMethods
            ?.cards,
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.paymentMethods?.[EPaymentType.MOYASAR] || []
        ) ||
        !arraysAreEqual(
          watch(`milestones.${watch("currentMilestoneIndex")}`)?.paymentMethods
            ?.tabby,
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.paymentMethods?.[EPaymentType.TABBY] || []
        ) ||
        !arraysAreEqual(
          watch(`milestones.${watch("currentMilestoneIndex")}`)?.paymentMethods
            ?.bank_transfer,
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.paymentMethods?.[EPaymentType.MANUAL_BANK_TRANSFER] || []
        ) ||
        !arraysAreEqual(
          watch(`milestones.${watch("currentMilestoneIndex")}`)?.paymentMethods
            ?.stc_pay,
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.paymentMethods?.[EPaymentType.STCPAY] || []
        ) ||
        !arraysAreEqual(
          watch(`milestones.${watch("currentMilestoneIndex")}`)?.images,
          [
            ...(draftWatch("createdMilestones")?.[
              watch("currentMilestoneIndex")
            ]?.itemDetail?.images || []),
            ...(draftWatch("createdMilestones")?.[
              watch("currentMilestoneIndex")
            ]?.itemDetail?.attachments || []),
          ]
        ) ||
        !!watch(`milestones.${watch("currentMilestoneIndex")}`)?.buyerOnly !==
          !!draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.buyerOnly ||
        (watch(`milestones.${watch("currentMilestoneIndex")}`)
          ?.sourceContractId || "") !==
          (draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.sourceContractId || "")
      )
        response = await editExternalContractDetails(
          {
            ...milestoneContractBody(
              ESenderRole.BROKER,
              milestone?.title,
              milestone?.images?.filter((file) => !file?.endsWith("pdf")) || [],
              milestone?.images?.filter((file) => file?.endsWith("pdf")) || [],
              milestone?.parties
                ?.filter((party) => party?.type === "customer")
                ?.reduce((acc, cur) => +acc + +cur?.amount, 0),
              milestone?.paymentMethods,
              milestone?.deadline,
              milestone?.milestoneCancelTerms,
              milestone?.milestoneReleaseTerms,
              isBEDraft,
              draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
                ?.isAcceptable,
              draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
                ?.isInspectable,
              currentStep.toString()
            ),
            ...(milestone?.buyerOnly ? { buyerOnly: true } : {}),
            ...(milestone?.sourceContractId
              ? { sourceContractId: milestone.sourceContractId }
              : {}),
            ...(milestone?.isDestination
              ? {
                  paymentMethods: {
                    [EPaymentType.CONTRACT_TRANSFER]: [
                      EPaymentBrand.CONTRACT_TRANSFER,
                    ],
                  },
                }
              : {}),
          },
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]?.id
        );

      if (response) {
        if (
          draftWatch("createdMilestones")?.length === 0 ||
          watch("currentMilestoneIndex") >
            draftWatch("createdMilestones")?.length - 1
        )
          setDraftFormValue(
            `createdMilestones.${watch("currentMilestoneIndex")}`,
            response?.data?.milestones?.[
              isAddMilestone ? 0 : watch("currentMilestoneIndex")
            ]
          );
        else
          setDraftFormValue(
            `createdMilestones.${watch("currentMilestoneIndex")}`,
            response?.data
          );
      }
    } catch (error) {
      const currentIdx = watch("currentMilestoneIndex");
      const isDestination = !!watch(`milestones.${currentIdx}.isDestination`);
      const msg =
        error && typeof error === "object" && "error" in error
          ? (error as { error?: { message?: string } }).error?.message
          : undefined;
      if (msg && isDestination) {
        setError(`milestones.${currentIdx}.sourceContractId`, {
          message: msg,
        });
      } else {
        console.error(error);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const draftStepThree = async (withErrors?: boolean) => {
    try {
      setLoading(true);
      let response;
      const currentMilestoneIndex = watch("currentMilestoneIndex");
      const milestone = watch(`milestones.${currentMilestoneIndex}`);

      let partiesMap;

      const savedDraftParties =
        draftWatch("createdMilestones")?.[currentMilestoneIndex]?.parties || [];

      // Find saved broker only if it matches the org's accountUserIdentifier
      // (step 2 may have created a wrong client-admin broker — ignore it)
      const savedBrokerParty = savedDraftParties.find(
        (p) =>
          p?.role === ESenderRole.BROKER &&
          p?.userId === String(organization?.accountUserIdentifier)
      );

      // True when draft has a broker with the WRONG userId (client admin from step 2)
      const brokerNeedsReplacement = savedDraftParties.some(
        (p) =>
          p?.role === ESenderRole.BROKER &&
          p?.userId !== String(organization?.accountUserIdentifier)
      );

      // Always build broker entry from the org's accountUserIdentifier — never
      // carry over the client-admin broker that the server creates in step 2.
      const orgBrokerEntry = organization?.accountUserIdentifier
        ? {
            userId: String(organization.accountUserIdentifier),
            role: ESenderRole.BROKER,
            amount: savedBrokerParty?.amount || 0,
            ...(savedBrokerParty?.fee ? { fee: savedBrokerParty.fee } : {}),
            sudoName: organization?.name || savedBrokerParty?.sudoName,
            arbitrator: true,
            isSender: true,
          }
        : null;

      const mapParty = (party) => {
        const isBroker = party?.role === ESenderRole.BROKER;
        const isCompany = !!party?.companyUnifiedNumber;
        let identityFields: Record<string, unknown> = {};
        if (isBroker) {
          identityFields = {
            userId: (party?.phoneNumber || "")?.toString()?.trim(),
          };
        } else {
          const raw = (party?.phoneNumber || "")?.toString()?.trim();
          identityFields = {
            phoneNumber: raw.startsWith("+966") ? raw : `+966${raw}`,
          };
          if (isCompany) {
            identityFields.companyUnifiedNumber = (
              party?.companyUnifiedNumber || ""
            )
              ?.toString()
              ?.trim();
            identityFields.companyName = (party?.companyName || "")
              ?.toString()
              ?.trim();
          }
        }
        return {
          ...identityFields,
          role: party?.type?.toUpperCase() as ESenderRole,
          amount: party?.amount,
          sudoName: party?.name,
          ...(party?.fee ? { fee: party?.fee } : {}),
          ...(isBroker
            ? { role: ESenderRole.BROKER, arbitrator: true, isSender: true }
            : {}),
        };
      };

      {
        const buyerOnly = !!milestone?.buyerOnly;
        const draftPartiesLength =
          draftWatch("createdMilestones")?.[currentMilestoneIndex]?.parties
            ?.length;
        const customersChanged =
          draftPartiesLength !== 0 &&
          !milestonePartiesEqual(
            savedDraftParties?.filter(
              (party) => party?.role === ESenderRole.CUSTOMER
            ),
            milestone?.parties?.filter((party) => party?.type === "customer")
          );

        const brokerPresent = savedDraftParties.some(
          (p) => p?.role === ESenderRole.BROKER
        );
        const shouldUpdateParties =
          draftPartiesLength === 1 ||
          brokerNeedsReplacement ||
          customersChanged ||
          (buyerOnly && brokerPresent);

        if (shouldUpdateParties) {
          const mappedBuyers =
            milestone?.parties
              ?.filter((p) => p?.role !== ESenderRole.BROKER)
              ?.map(mapParty) || [];

          partiesMap = {
            [draftWatch("createdMilestones")?.[currentMilestoneIndex]?.id]:
              buyerOnly
                ? mappedBuyers.map((p, idx) => ({
                    ...p,
                    ...(idx === 0 ? { isSender: true } : {}),
                  }))
                : [
                    ...mappedBuyers,
                    ...(orgBrokerEntry ? [orgBrokerEntry] : []),
                  ],
          };
        }

        if (withErrors && partiesMap) {
          const milestoneId =
            draftWatch("createdMilestones")?.[currentMilestoneIndex]?.id || "";
          response = await editExternalDraftContractParties(milestoneId, {
            parties: partiesMap?.[milestoneId] || [],
          });
        } else if (shouldUpdateParties) {
          response = await editContractPartiesExternal(
            draftWatch("createdComplex")?.id || "",
            {
              mileStonesParties: partiesMap || {},
            }
          );
        }
      }

      // Save progress
      await editExternalContractDetails(
        {
          ...milestoneContractBody(
            ESenderRole.BROKER,
            milestone?.title,
            milestone?.images?.filter((file) => !file?.endsWith("pdf")) || [],
            milestone?.images?.filter((file) => file?.endsWith("pdf")) || [],
            milestone?.parties
              ?.filter((party) => party?.type === "customer")
              ?.reduce((acc, cur) => +acc + +cur?.amount, 0),
            milestone?.paymentMethods,
            milestone?.deadline,
            milestone?.milestoneCancelTerms,
            milestone?.milestoneReleaseTerms,
            isBEDraft,
            draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
              ?.isAcceptable,
            draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
              ?.isInspectable,
            currentStep.toString()
          ),
          ...(milestone?.buyerOnly ? { buyerOnly: true } : {}),
          ...(milestone?.sourceContractId
            ? { sourceContractId: milestone.sourceContractId }
            : {}),
          ...(milestone?.isDestination
            ? {
                paymentMethods: {
                  [EPaymentType.CONTRACT_TRANSFER]: [
                    EPaymentBrand.CONTRACT_TRANSFER,
                  ],
                },
              }
            : {}),
        },
        draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]?.id
      );

      if (response)
        setDraftFormValue(
          `createdMilestones.${currentMilestoneIndex}.parties`,
          partiesMap?.[
            `${draftWatch(`createdMilestones.${currentMilestoneIndex}`)?.id}`
          ]?.map((party) => ({
            ...party,
            ...(party?.companyUnifiedNumber ? { isCompany: true } : {}),
            name:
              milestone?.parties?.find((partyObj) => {
                if (party?.role === ESenderRole.BROKER)
                  return partyObj?.role === ESenderRole.BROKER;
                if (party?.companyUnifiedNumber)
                  return (
                    partyObj?.companyUnifiedNumber ===
                    party?.companyUnifiedNumber
                  );
                return partyObj?.phoneNumber === party?.phoneNumber;
              })?.name || party?.sudoName,
          }))
        );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const draftStepFour = async (withErrors?: boolean) => {
    try {
      setLoading(true);
      let response;
      const currentMilestoneIndex = watch("currentMilestoneIndex");
      const milestone = watch(`milestones.${currentMilestoneIndex}`);
      const buyerOnly = !!watch(
        `milestones.${currentMilestoneIndex}.buyerOnly`
      );

      let partiesMap;
      const milId =
        draftWatch("createdMilestones")?.[currentMilestoneIndex]?.id || "";

      if (buyerOnly) {
        // Without-sellers mode: send only customer parties; first customer gets isSender
        const customerParties =
          milestone?.parties?.filter((p) => p?.type === "customer") || [];
        if (customerParties.length > 0) {
          partiesMap = {
            [milId]: customerParties.map((party, idx) => {
              const raw = (party?.phoneNumber || "")?.toString()?.trim();
              const isCompany = !!party?.companyUnifiedNumber;
              const identityFields: Record<string, unknown> = {
                phoneNumber: raw.startsWith("+966") ? raw : `+966${raw}`,
                ...(isCompany
                  ? {
                      companyUnifiedNumber: (party?.companyUnifiedNumber || "")
                        ?.toString()
                        ?.trim(),
                      companyName: (party?.companyName || "")
                        ?.toString()
                        ?.trim(),
                    }
                  : {}),
              };
              return {
                ...identityFields,
                role: "CUSTOMER" as ESenderRole,
                amount: party?.amount,
                sudoName: party?.name,
                ...(party?.fee ? { fee: party?.fee } : {}),
                ...(idx === 0 ? { isSender: true } : {}),
              };
            }),
          };
          if (withErrors) {
            response = await editExternalDraftContractParties(milId, {
              parties: partiesMap[milId] || [],
            });
          } else {
            response = await editContractPartiesExternal(
              draftWatch("createdComplex")?.id || "",
              { mileStonesParties: partiesMap }
            );
          }
        }
      } else {
        const providersChanged =
          draftWatch("createdMilestones")?.[
            currentMilestoneIndex
          ]?.parties?.filter((party) => party?.role === ESenderRole.PROVIDER)
            ?.length === 0 ||
          !milestonePartiesEqual(
            draftWatch("createdMilestones")?.[
              currentMilestoneIndex
            ]?.parties?.filter((party) => party?.role === ESenderRole.PROVIDER),
            milestone?.parties?.filter(
              (party) =>
                party?.type === "provider" && party?.role !== ESenderRole.BROKER
            )
          ) ||
          !milestonePartiesEqual(
            draftWatch("createdMilestones")?.[
              currentMilestoneIndex
            ]?.parties?.filter((party) => party?.role === ESenderRole.BROKER),
            milestone?.parties?.filter(
              (party) => party?.role === ESenderRole.BROKER
            )
          );

        if (providersChanged) {
          const baseParties = milestone?.parties;

          // Dedup: one broker + unique non-brokers by identity key
          const seenKeys = new Set<string>();
          let brokerAdded = false;
          const dedupedParties = baseParties?.filter((party) => {
            if (party?.role === ESenderRole.BROKER) {
              if (brokerAdded) return false;
              brokerAdded = true;
              return true;
            }
            const identityKey = String(party?.phoneNumber || "").replaceAll(
              " ",
              ""
            );
            if (!identityKey) return true;
            const key = `${party.role ?? party.type ?? ""}:${identityKey}`;
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
          });

          partiesMap = {
            [milId]: [
              ...(dedupedParties?.map((party) => {
                const isBroker = party?.role === ESenderRole.BROKER;
                const isCompany = !!party?.companyUnifiedNumber;
                let identityFields: Record<string, unknown> = {};
                if (isBroker) {
                  identityFields = {
                    userId: (party?.phoneNumber || "")?.toString()?.trim(),
                  };
                } else {
                  const raw = (party?.phoneNumber || "")?.toString()?.trim();
                  identityFields = {
                    phoneNumber: raw.startsWith("+966") ? raw : `+966${raw}`,
                  };
                  if (isCompany) {
                    identityFields.companyUnifiedNumber = (
                      party?.companyUnifiedNumber || ""
                    )
                      ?.toString()
                      ?.trim();
                    identityFields.companyName = (party?.companyName || "")
                      ?.toString()
                      ?.trim();
                  }
                }
                return {
                  ...identityFields,
                  role: party?.type?.toUpperCase() as ESenderRole,
                  amount: party?.amount,
                  sudoName: party?.name,
                  sudoRole: party?.role,
                  ...(party?.fee ? { fee: party?.fee } : {}),
                  ...(isBroker
                    ? {
                        role: ESenderRole.BROKER,
                        arbitrator: true,
                        isSender: true,
                      }
                    : {}),
                };
              }) || []),
            ],
          };
        }

        if (withErrors && partiesMap) {
          response = await editExternalDraftContractParties(milId, {
            parties: partiesMap?.[milId] || [],
          });
        } else if (providersChanged) {
          response = await editContractPartiesExternal(
            draftWatch("createdComplex")?.id || "",
            {
              mileStonesParties: partiesMap || {},
            }
          );
        }
      }

      // Save progress
      await editExternalContractDetails(
        {
          ...milestoneContractBody(
            ESenderRole.BROKER,
            milestone?.title,
            milestone?.images?.filter((file) => !file?.endsWith("pdf")) || [],
            milestone?.images?.filter((file) => file?.endsWith("pdf")) || [],
            milestone?.parties
              ?.filter((party) => party?.type === "customer")
              ?.reduce((acc, cur) => +acc + +cur?.amount, 0),
            milestone?.paymentMethods,
            milestone?.deadline,
            milestone?.milestoneCancelTerms,
            milestone?.milestoneReleaseTerms,
            isBEDraft,
            draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
              ?.isAcceptable,
            draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
              ?.isInspectable,
            currentStep.toString()
          ),
          ...(milestone?.buyerOnly ? { buyerOnly: true } : {}),
          ...(milestone?.sourceContractId
            ? { sourceContractId: milestone.sourceContractId }
            : {}),
          ...(milestone?.isDestination
            ? {
                paymentMethods: {
                  [EPaymentType.CONTRACT_TRANSFER]: [
                    EPaymentBrand.CONTRACT_TRANSFER,
                  ],
                },
              }
            : {}),
        },
        draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]?.id
      );

      if (response)
        setDraftFormValue(
          `createdMilestones.${currentMilestoneIndex}.parties`,
          partiesMap?.[
            `${draftWatch(`createdMilestones.${currentMilestoneIndex}`)?.id}`
          ]?.map((party) => ({
            ...party,
            ...(party?.companyUnifiedNumber ? { isCompany: true } : {}),
            name:
              milestone?.parties?.find((partyObj) => {
                if (party?.role === ESenderRole.BROKER)
                  return partyObj?.role === ESenderRole.BROKER;
                if (party?.companyUnifiedNumber)
                  return (
                    partyObj?.companyUnifiedNumber ===
                    party?.companyUnifiedNumber
                  );
                return partyObj?.phoneNumber === party?.phoneNumber;
              })?.name || party?.sudoName,
          }))
        );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const draftStepFive = async () => {
    try {
      setLoading(true);
      let response;
      const milestone = watch(`milestones.${watch("currentMilestoneIndex")}`);

      if (
        new Date(
          watch(`milestones.${watch("currentMilestoneIndex")}`)?.deadline
        )?.toISOString() !==
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.deadLine ||
        watch(`milestones.${watch("currentMilestoneIndex")}`)
          ?.milestoneCancelTerms !==
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.cancellationConditions ||
        watch(`milestones.${watch("currentMilestoneIndex")}`)
          ?.milestoneReleaseTerms !==
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]
            ?.acceptanceConditions
      )
        response = await editExternalContractDetails(
          {
            ...milestoneContractBody(
              ESenderRole.BROKER,
              milestone?.title,
              milestone?.images?.filter((file) => !file?.endsWith("pdf")) || [],
              milestone?.images?.filter((file) => file?.endsWith("pdf")) || [],
              milestone?.parties
                ?.filter((party) => party?.type === "customer")
                ?.reduce((acc, cur) => +acc + +cur?.amount, 0),
              milestone?.paymentMethods,
              milestone?.deadline,
              milestone?.milestoneCancelTerms,
              milestone?.milestoneReleaseTerms,
              isBEDraft,
              draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
                ?.isAcceptable,
              draftWatch(`createdMilestones.${watch("currentMilestoneIndex")}`)
                ?.isInspectable,
              currentStep.toString()
            ),
            ...(milestone?.buyerOnly ? { buyerOnly: true } : {}),
            ...(milestone?.sourceContractId
              ? { sourceContractId: milestone.sourceContractId }
              : {}),
            ...(milestone?.isDestination
              ? {
                  paymentMethods: {
                    [EPaymentType.CONTRACT_TRANSFER]: [
                      EPaymentBrand.CONTRACT_TRANSFER,
                    ],
                  },
                }
              : {}),
          },
          draftWatch("createdMilestones")?.[watch("currentMilestoneIndex")]?.id
        );

      if (response)
        setDraftFormValue(
          `createdMilestones.${watch("currentMilestoneIndex")}`,
          {
            ...response?.data,
            parties: response?.data?.parties?.map((party) => ({
              ...party,
              ...(party?.companyUnifiedNumber ? { isCompany: true } : {}),
              name: response?.data?.partiesProfiles?.[party?.role]?.find(
                (partyObj) => partyObj?.phoneNumber === party?.phoneNumber
              )?.profile?.fullName,
            })),
          }
        );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <FormProvider {...methods}>
        <Header
          isDirty={isDirty}
          createdId={
            isAddMilestone
              ? draftWatch(
                  `createdMilestones.${watch("currentMilestoneIndex")}`
                )?.id
              : draftWatch("createdComplex")?.id
          }
        />

        {loading || resetLoading ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Spinner />
          </div>
        ) : (
          <form
            style={{
              height: "calc(100% - 36px)",
            }}
            onSubmit={handleSubmit(onSubmit)}>
            {currentStep === 0 && (
              <StepOne
                handleNext={async () => {
                  setCurrentStep((prev) => prev + 1);
                  if (!isEditCreatedMilestone) {
                    draftStepOne();
                  }
                }}
              />
            )}
            {currentStep === 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  height: "100%",
                }}>
                {resetLoading ? (
                  <Spinner />
                ) : (
                  <StepTwo
                    fileUploadref={fileUploadref}
                    currentStep={currentStep}
                    handleSaveDraft={
                      isEditCreatedMilestone
                        ? noOpDraftSave
                        : async () => {
                            await draftStepTwo();
                            navigate(
                              ROUTES.CONTRACT_DETAILS?.replace(
                                ":id",
                                draftWatch("createdComplex")?.id
                              )
                            );
                          }
                    }
                    loading={loading}
                    handleNext={async () => {
                      if (!isEditCreatedMilestone) {
                        try {
                          await draftStepTwo();
                        } catch {
                          return;
                        }
                      }
                      setCurrentStep((prev) => prev + 1);
                    }}
                    handlePrev={
                      isEditMode || isAddMilestone
                        ? undefined
                        : () => setCurrentStep((prev) => prev - 1)
                    }
                  />
                )}
              </div>
            )}
            {currentStep === 2 && (
              <StepThree
                currentStep={currentStep}
                loading={loading}
                handleNext={() => {
                  const isBuyerOnly = !!watch(
                    `milestones.${watch("currentMilestoneIndex")}.buyerOnly`
                  );
                  setCurrentStep(isBuyerOnly ? 4 : currentStep + 1);

                  if (!isEditCreatedMilestone) {
                    draftStepThree();
                  }
                }}
                handleSaveDraft={
                  isEditCreatedMilestone
                    ? noOpDraftSave
                    : async () => {
                        await draftStepThree(true);
                        navigate(
                          ROUTES.CONTRACT_DETAILS?.replace(
                            ":id",
                            draftWatch("createdComplex")?.id
                          )
                        );
                      }
                }
                handlePrev={() => setCurrentStep((prev) => prev - 1)}
              />
            )}
            {currentStep === 3 && (
              <StepFour
                currentStep={currentStep}
                loading={loading}
                handleNext={() => {
                  setCurrentStep((prev) => prev + 1);

                  if (!isEditCreatedMilestone) {
                    draftStepFour();
                  }
                }}
                handleSaveDraft={
                  isEditCreatedMilestone
                    ? noOpDraftSave
                    : async () => {
                        await draftStepFour(true);
                        navigate(
                          ROUTES.CONTRACT_DETAILS?.replace(
                            ":id",
                            draftWatch("createdComplex")?.id
                          )
                        );
                      }
                }
                handlePrev={() => setCurrentStep((prev) => prev - 1)}
              />
            )}
            {currentStep === 4 && (
              <StepFive
                currentStep={currentStep}
                loading={loading}
                handleNext={async () => {
                  // Edit Created
                  // Auto save (draft flow) handles this but it shouldn't -> we will disable the draft flow here and send the data normally as PUT request
                  // There is no save as draft option and no need for step six

                  if (isEditCreatedMilestone) {
                    // When editing a created contract, perform a clean edit operation
                    await editCreatedMilestone();
                  } else {
                    await draftStepFive();
                    await handleFormSubmit();
                  }
                }}
                handleSaveDraft={
                  isEditCreatedMilestone
                    ? noOpDraftSave
                    : async (isPreviewMilestone) => {
                        await draftStepFive();
                        navigate(
                          ROUTES.CONTRACT_DETAILS?.replace(
                            ":id",
                            isPreviewMilestone
                              ? draftWatch("createdMilestones")?.[
                                  watch("currentMilestoneIndex")
                                ]?.id
                              : draftWatch("createdComplex")?.id
                          )
                        );
                      }
                }
                handlePrev={() => {
                  const isBuyerOnly = !!watch(
                    `milestones.${watch("currentMilestoneIndex")}.buyerOnly`
                  );
                  setCurrentStep(isBuyerOnly ? 2 : currentStep - 1);
                }}
                isEditCreatedMilestone={isEditCreatedMilestone}
              />
            )}
            {/* {currentStep === 5 && !isEditCreatedMilestone && (
              <StepSix
                milestonesArray={watch("milestones")?.map(
                  (milestone, index) => ({
                    ...milestone,
                    status: draftWatch(`createdMilestones.${index}`).status,
                  })
                )}
                loading={loading}
                handleAddMilestone={() => {
                  addMilestone({
                    title: "",
                    paymentMethods: {
                      cards: [],
                      apple_pay: [],
                      bank_transfer: [],
                      tabby: [],
                    },
                    parties: [],
                    images: [],
                    milestoneCancelTerms: "",
                    milestoneReleaseTerms: "",
                    deadline: new Date()?.toString(),
                  });
                  setValue(
                    "currentMilestoneIndex",
                    watch("currentMilestoneIndex") + 1
                  );
                  setCurrentStep(1);
                }}
                handleDeleteMilestone={async (index) => {
                  removeMilestone(index);
                  await deleteExternalContractById(
                    draftWatch(`createdMilestones.${index}`)?.id
                  );
                  removeDraftMilestone(index);
                }}
                handleEditMilestone={(index) => {
                  setValue("currentMilestoneIndex", index);
                  setCurrentStep(1);
                }}
                handleSaveDraft={(data) => {
                  if (isEditMode)
                    navigate(
                      ROUTES.CONTRACT_DETAILS?.replace(
                        ":id",
                        draftWatch(
                          `createdMilestones.${watch("currentMilestoneIndex")}`
                        )?.id
                      )
                    );
                  else if (isAddMilestone)
                    navigate(
                      ROUTES.CONTRACT_DETAILS?.replace(
                        ":id",
                        draftWatch("createdComplex")?.id
                      )
                    );
                  else navigate(ROUTES.HOME);
                }}
                handleSave={handleFormSubmit}
                handlePrev={() => setCurrentStep((prev) => prev - 1)}
              />
            )} */}
          </form>
        )}
      </FormProvider>
    </div>
  );
};

export default CreateContract;
